import {
	useApiCreatePolicyRule,
	useApiDeletePolicyRule,
	useApiListPolicyRules,
	useApiPatchPolicyRule,
	type ApiCreatePolicyRuleError,
	type ApiCreatePolicyRuleVariables,
	type ApiDeletePolicyRuleError,
	type ApiDeletePolicyRuleVariables,
	type ApiListPolicyRulesError,
	type ApiListPolicyRulesVariables,
	type ApiPatchPolicyRuleError,
	type ApiPatchPolicyRuleVariables,
	type PagedPolicyRuleOut,
	type PolicyRuleOut,
} from '@repo/api-codegen';
import { UserRole } from '@repo/common/enums/UserRole';
import type {
	UseMutationOptions,
	UseQueryOptions,
} from '@tanstack/react-query';
import { queryClient } from '../../';
import { useCheckIAMPermission } from '../../../utils/authorization/roles';
import { createQueryKeys } from '../../factories';

export const POLICIES_NAMESPACE = ['policy'];
export const POLICY_RULES_NAMESPACE = ['policy', 'rules'];
export const policiesQueryKeyFactory = createQueryKeys(POLICIES_NAMESPACE);
export const policyRulesQueryKeyFactory = createQueryKeys(
	POLICY_RULES_NAMESPACE
);

export const usePolicyWritePermission = () => {
	const { hasPermission: checkIamResult } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		v2Permission: 'Policies.Manage',
	});

	return checkIamResult;
};

export const usePolicyReadPermission = () => {
	const { hasPermission: checkIamResult } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.VIEWER],
		v2Permission: 'Policies.Read',
	});

	return checkIamResult;
};

// Policy Rules Hooks

export function useListPolicyRules(
	variables: ApiListPolicyRulesVariables,
	options?: Omit<
		UseQueryOptions<PagedPolicyRuleOut, ApiListPolicyRulesError>,
		'queryKey' | 'queryFn'
	>
) {
	return useApiListPolicyRules(variables, options);
}

export function useCreatePolicyRule(
	options?: UseMutationOptions<
		PolicyRuleOut,
		ApiCreatePolicyRuleError,
		ApiCreatePolicyRuleVariables
	>
) {
	return useApiCreatePolicyRule({
		...options,
		onSuccess: (
			data: PolicyRuleOut,
			variables: ApiCreatePolicyRuleVariables,
			context: unknown
		) => {
			// Invalidate policy rules list for this policy
			queryClient.invalidateQueries({
				queryKey: ['policy', variables.pathParams.policyId, 'rules'],
			});
			options?.onSuccess?.(data, variables, context);
		},
	});
}

export function useUpdatePolicyRule(
	options?: UseMutationOptions<
		PolicyRuleOut,
		ApiPatchPolicyRuleError,
		ApiPatchPolicyRuleVariables
	>
) {
	return useApiPatchPolicyRule({
		...options,
		onSuccess: (
			data: PolicyRuleOut,
			variables: ApiPatchPolicyRuleVariables,
			context: unknown
		) => {
			// Invalidate policy rules list for this policy
			queryClient.invalidateQueries({
				queryKey: ['policy', variables.pathParams.policyId, 'rules'],
			});
			options?.onSuccess?.(data, variables, context);
		},
	});
}

export function useDeletePolicyRule(
	options?: UseMutationOptions<
		void,
		ApiDeletePolicyRuleError,
		ApiDeletePolicyRuleVariables
	>
) {
	return useApiDeletePolicyRule({
		...options,
		onSuccess: (
			data: void,
			variables: ApiDeletePolicyRuleVariables,
			context: unknown
		) => {
			// Invalidate policy rules list for this policy
			queryClient.invalidateQueries({
				queryKey: ['policy', variables.pathParams.policyId, 'rules'],
			});
			options?.onSuccess?.(data, variables, context);
		},
	});
}
